import { useChat } from '@ai-sdk/react';
import { DefaultChatTransport } from 'ai';
import type { UIMessage } from 'ai';
import { v4 as uuidv4 } from 'uuid';
import { getAccessTokenFromStorage } from '../../utils';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AiChat, Message, MessageRole } from '../../@types';
import { useRouter } from 'next/router';

interface Payload {
  currentChat: AiChat;
  refetchAiChatData: () => void;
  modelId?: string;
}

export const useAiChat = ({ currentChat, refetchAiChatData, modelId }: Payload) => {
  const [chatId, setChatId] = useState(currentChat?.id || uuidv4());
  const [initialTextMessages, setInitialTextMessages] = useState<
    Array<UIMessage & Partial<Message>>
  >((currentChat?.messages as any) ?? []);
  const [input, setInput] = useState('');

  const selectedModelIdRef = useRef<string | undefined>(modelId);
  useEffect(() => {
    selectedModelIdRef.current = modelId;
  }, [modelId]);

  const { query, push, pathname } = useRouter();
  const pushedChatIdRef = useRef<string | null>(null);
  const rawRouterChatId = query?.['chatId'];
  const routerChatId = Array.isArray(rawRouterChatId) ? rawRouterChatId[0] : rawRouterChatId;
  const normalizedRouterChatId =
    typeof routerChatId === 'string' && routerChatId !== 'undefined' && routerChatId.length > 0
      ? routerChatId
      : undefined;

  const transport = useMemo(() => {
    return new DefaultChatTransport<UIMessage>({
      api: `${process?.env?.NEXT_PUBLIC_API_BASE_URL}/api/script/chat`,
      headers: () => ({
        access_token: getAccessTokenFromStorage() || '',
      }),
      body: () => ({
        modelId: selectedModelIdRef.current,
      }),
    });
  }, []);

  const {
    status,
    messages: uiMessages,
    sendMessage,
    setMessages,
  } = useChat<UIMessage>({
    id: chatId,
    messages: initialTextMessages as any,
    transport,
  });
  const isLoading = status === 'submitted' || status === 'streaming';

  const currentChatId = currentChat?.id;
  const currentChatMessages = currentChat?.messages;

  const resetChat = useCallback(() => {
    pushedChatIdRef.current = null;
    const nextChatId = uuidv4();
    setChatId(nextChatId);
    setInitialTextMessages([]);
    setMessages([]);
    setInput('');
  }, [setMessages]);

  const messages: Message[] = useMemo(() => {
    return (uiMessages ?? []).map((m: any) => {
      const textParts = Array.isArray(m?.parts)
        ? m.parts.filter((p: any) => p?.type === 'text' && typeof p?.text === 'string')
        : [];

      const contentFromParts = textParts.map((p: any) => p.text).join('');

      return {
        id: typeof m?.id === 'string' ? m.id : uuidv4(),
        role: (m?.role ?? 'user') as MessageRole,
        parts: textParts.map((p: any) => ({ type: 'text', text: p.text })),
        content:
          typeof m?.content === 'string' && m.content.length > 0 ? m.content : contentFromParts,
        createdAt: m?.createdAt ? new Date(m.createdAt) : new Date(),
        revisionId: m?.revisionId,
      };
    });
  }, [uiMessages]);

  const handleInputChange = (
    event: React.ChangeEvent<HTMLInputElement> | React.ChangeEvent<HTMLTextAreaElement>
  ) => {
    setInput(event.target.value);
  };

  const handleSubmit = async (event?: { preventDefault?: () => void }) => {
    event?.preventDefault?.();

    const trimmed = input.trim();
    if (!trimmed) return;

    // Clear immediately so the textarea doesn't keep the submitted text
    // until the streaming request finishes.
    setInput('');
    await sendMessage({ parts: [{ type: 'text', text: trimmed }] });
  };

  useEffect(() => {
    // When switching between chats (or starting a new one), we need to reset the
    // underlying `useChat` message state immediately; otherwise a "new chat" click
    // can momentarily satisfy the "first assistant reply" URL-push logic using the
    // PREVIOUS chat's messages.
    pushedChatIdRef.current = null;

    if (currentChatId) {
      const nextChatId = currentChatId;
      const nextMessages = ((currentChatMessages as any) ?? []) as any;
      setChatId(nextChatId);
      setInitialTextMessages(nextMessages);
      setMessages(nextMessages);
      return;
    }

    setChatId(uuidv4());
    setInitialTextMessages([]);
    setMessages([]);
  }, [currentChatId, currentChatMessages, setMessages]);

  useEffect(() => {
    // Only set the URL chatId + refresh history once, when a NEW chat gets its first assistant reply.
    // When viewing an existing chat, doing this would cause a refetch/push loop.
    if (currentChat) return;
    if (isLoading) return;
    if (uiMessages.length < 2) return;
    if (normalizedRouterChatId === chatId) return;
    if (pushedChatIdRef.current === chatId) return;

    pushedChatIdRef.current = chatId;
    refetchAiChatData();

    void push(
      {
        pathname,
        query: { ...query, chatId },
      },
      undefined,
      { shallow: true }
    );
  }, [
    chatId,
    currentChat,
    isLoading,
    normalizedRouterChatId,
    pathname,
    push,
    query,
    refetchAiChatData,
    uiMessages.length,
  ]);

  return {
    input,
    isLoading,
    handleInputChange,
    handleSubmit,
    messages,
    setInput,
    resetChat,
  };
};
